/**
 * Pricing Calculator Service
 *
 * ROI calculation utilities for Replenishment Center pricing page
 * Features:
 * - Time savings calculator with diminishing returns
 * - Stockout prevention calculator
 * - Annual savings calculator
 * - ROI percentage calculator
 *
 * Created: 2025-10-30
 * Related: replenishment-center-first-month-discount feature
 */

/**
 * Calculate time saved per month based on SKU count
 * Uses simplified formula with base hours + incremental growth
 * Supports up to 50,000 SKUs for large enterprise merchants
 *
 * @param skuCount - Number of SKUs in merchant's catalog
 * @returns Hours saved per month
 *
 * Formula:
 * - Base: ~10 hours for stores with <1000 SKUs
 * - Growth: +1 hour per additional 10,000 SKUs above 1000
 *
 * Examples:
 * - 500 SKUs = 10 hours (base)
 * - 1000 SKUs = 10 hours (base)
 * - 11000 SKUs = 11 hours (10 + 1 hour for 10k additional)
 * - 21000 SKUs = 12 hours (10 + 2 hours for 20k additional)
 * - 50000 SKUs = 14.9 hours (10 + 4.9 hours for 49k additional)
 */
export function calculateTimeSaved(skuCount: number): number {
  let timeSaved: number;

  if (skuCount <= 1000) {
    // Base: ~10 hours for stores with <1000 SKUs
    timeSaved = 10;
  } else {
    // Above 1000 SKUs: +1 hour per 10,000 additional SKUs
    const additionalSkus = skuCount - 1000;
    const additionalHours = (additionalSkus / 10000) * 1;
    timeSaved = 10 + additionalHours;
  }

  // Round to 1 decimal place
  return Math.round(timeSaved * 10) / 10;
}

/**
 * Calculate monthly revenue protected from stockout prevention
 * Uses 1% average stockout loss rate
 *
 * @param monthlyOrders - Average monthly order count
 * @param orderValue - Average order value ($)
 * @returns Monthly revenue protected ($)
 *
 * Formula:
 * - Average stockout rate: 1% of total revenue
 * - Total monthly revenue = monthlyOrders * orderValue
 * - Revenue at risk = total * 0.01
 *
 * Examples:
 * - 1000 orders/month @ $50 = $500 protected
 * - 5000 orders/month @ $75 = $3,750 protected
 */
export function calculateStockoutPrevention(
  monthlyOrders: number,
  orderValue: number
): number {
  const totalMonthlyRevenue = monthlyOrders * orderValue;
  const revenueProtected = totalMonthlyRevenue * 0.01;

  // Round to 2 decimal places
  return Math.round(revenueProtected * 100) / 100;
}

/**
 * Calculate annual net savings after paying for Replenishment Center
 * Shows total value delivered to merchant including both labor savings and revenue protection
 *
 * @param monthlyStockout - Monthly revenue protected from stockouts
 * @param monthlyLaborSavings - Monthly labor cost savings from automation
 * @param rcMonthlyCost - Replenishment Center monthly subscription cost
 * @returns Annual net savings ($)
 *
 * Formula:
 * - Annual stockout benefit = monthlyStockout * 12
 * - Annual labor benefit = monthlyLaborSavings * 12
 * - Annual cost = rcMonthlyCost * 12
 * - Net savings = (stockout benefit + labor benefit) - cost
 *
 * Examples:
 * - $2,500 protected + $200 labor - $249 RC = $29,412 annual savings
 * - $500 protected + $200 labor - $79 RC = $7,464 annual savings
 */
export function calculateAnnualSavings(
  monthlyStockout: number,
  monthlyLaborSavings: number,
  rcMonthlyCost: number
): number {
  const annualStockoutBenefit = monthlyStockout * 12;
  const annualLaborBenefit = monthlyLaborSavings * 12;
  const annualCost = rcMonthlyCost * 12;
  const netSavings = annualStockoutBenefit + annualLaborBenefit - annualCost;

  // Round to 2 decimal places
  return Math.round(netSavings * 100) / 100;
}

/**
 * Calculate ROI percentage for Replenishment Center investment
 * Most impressive metric for CFO approval
 *
 * @param monthlyStockout - Monthly revenue protected from stockouts
 * @param rcMonthlyCost - Replenishment Center monthly subscription cost
 * @returns ROI percentage (e.g., 3040 for 3040%)
 *
 * Formula:
 * - ROI% = (monthlyStockout / rcMonthlyCost) * 100
 *
 * Examples:
 * - $4,500 protected / $249 cost = 1807% ROI
 * - $600 protected / $79 cost = 759% ROI
 */
export function calculateROI(
  monthlyStockout: number,
  rcMonthlyCost: number
): number {
  if (rcMonthlyCost === 0) {
    return 0; // Prevent division by zero
  }

  const roi = (monthlyStockout / rcMonthlyCost) * 100;

  // Round to whole number
  return Math.round(roi);
}

/**
 * Calculate monthly labor cost savings from time automation
 * Converts time saved into dollar value based on hourly rate
 *
 * @param timeSaved - Hours saved per month from automation
 * @param hourlyRate - Labor cost per hour (default: $20)
 * @returns Monthly labor cost savings ($)
 *
 * Formula:
 * - Labor savings = timeSaved * hourlyRate
 *
 * Examples:
 * - 10 hours saved @ $20/hour = $200 saved
 * - 15 hours saved @ $20/hour = $300 saved
 * - 50 hours saved @ $20/hour = $1,000 saved
 */
export function calculateLaborCostSavings(
  timeSaved: number,
  hourlyRate: number = 20
): number {
  const laborSavings = timeSaved * hourlyRate;

  // Round to 2 decimal places
  return Math.round(laborSavings * 100) / 100;
}

/**
 * Example usage and testing:
 *
 * // Small merchant (500 SKUs)
 * calculateTimeSaved(500);              // 10 hours (base)
 *
 * // Small merchant (1000 SKUs)
 * calculateTimeSaved(1000);             // 10 hours (base)
 *
 * // Medium merchant (11000 SKUs)
 * calculateTimeSaved(11000);            // 11 hours (10 + 1)
 *
 * // Large merchant (21000 SKUs)
 * calculateTimeSaved(21000);            // 12 hours (10 + 2)
 *
 * // Enterprise merchant (31000 SKUs)
 * calculateTimeSaved(31000);            // 13 hours (10 + 3)
 *
 * // Max enterprise (50000 SKUs)
 * calculateTimeSaved(50000);            // 14.9 hours (10 + 4.9)
 *
 * // Full ROI calculation
 * const skuCount = 5000;
 * const monthlyOrders = 5000;
 * const orderValue = 75;
 * const rcMonthlyCost = 249;
 *
 * const timeSaved = calculateTimeSaved(skuCount);           // 10.4 hours
 * const laborSavings = calculateLaborCostSavings(timeSaved); // $208
 * const stockoutPrevented = calculateStockoutPrevention(
 *   monthlyOrders,
 *   orderValue
 * );                                                         // $3,750
 * const annualSavings = calculateAnnualSavings(
 *   stockoutPrevented,
 *   laborSavings,
 *   rcMonthlyCost
 * );                                                         // $42,024
 * const roi = calculateROI(stockoutPrevented, rcMonthlyCost); // 1506%
 */
